// ============================================
// Seed Templates: User-Facing Legal Documents
// Category: user-facing-legal
// Templates: 8
// ============================================

import type { DocTemplate, QualityGate } from '../../types/docops';

// ── TOS QUALITY GATE v2 ──
// 25 mandatory provisions (14 critical + 11 high)
// Scoring: critical=70%, high=30%. Pass: score >= 95 AND zero critical missing.
const TOS_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    // ── CRITICAL (70% of score) ──
    {
      id: 'tos-age-misrep',
      title: 'Age Misrepresentation Consequences',
      required: true,
      keywords: ['misrepresented your age', 'delete all associated data', '24 hours'],
      weight: 'critical',
    },
    {
      id: 'tos-edu-tool',
      title: 'Educational Tool Positioning',
      required: true,
      keywords: ['educational comparison tool', 'educational purposes only', 'educational purposes'],
      weight: 'critical',
    },
    {
      id: 'tos-ai-error',
      title: 'AI Error Disclaimer',
      required: true,
      keywords: ['may contain errors', 'inaccuracies'],
      weight: 'critical',
    },
    {
      id: 'tos-cpom-no-practice',
      title: 'CPOM Firewall - No Practice',
      required: true,
      keywords: ['does not practice medicine', 'technology platform', 'technology company'],
      weight: 'critical',
    },
    {
      id: 'tos-cpom-no-employ',
      title: 'CPOM Firewall - No Employment',
      required: true,
      keywords: ['does not employ', 'does not control', 'clinical decisions'],
      weight: 'critical',
    },
    {
      id: 'tos-no-guarantee-rx',
      title: 'No Guarantee of Prescription',
      required: true,
      keywords: ['does not guarantee', 'prescription', 'clinical judgment'],
      weight: 'critical',
    },
    {
      id: 'tos-consent-model',
      title: 'Granular Consent Model (7 purposes)',
      required: true,
      keywords: ['core service', 'health data analysis', 'marketing communications', 'withdraw consent'],
      weight: 'critical',
    },
    {
      id: 'tos-consent-withdraw',
      title: 'Consent Withdrawal Mechanics',
      required: true,
      keywords: ['withdraw consent', 'account deactivation', 'optional purposes'],
      weight: 'critical',
    },
    {
      id: 'tos-licensed-ip',
      title: 'Licensed IP Model',
      required: true,
      keywords: ['intellectual property owner', 'licensed to', 'exclusive license'],
      weight: 'critical',
    },
    {
      id: 'tos-clinical-carveout',
      title: 'Clinical Liability Carve-Out',
      required: true,
      keywords: ['not liable for clinical decisions', 'treating provider'],
      weight: 'critical',
    },
    {
      id: 'tos-class-action',
      title: 'Class Action Waiver',
      required: true,
      keywords: ['class action', 'individual basis', 'waive'],
      weight: 'critical',
    },
    {
      id: 'tos-chd-policy',
      title: 'Consumer Health Data Policy',
      required: true,
      keywords: ['consumer health data', 'health data policy', 'mhmda'],
      weight: 'critical',
    },
    {
      id: 'tos-privacy-ref',
      title: 'Privacy Policy Reference',
      required: true,
      keywords: ['privacy policy', 'privacy@'],
      weight: 'critical',
    },
    {
      id: 'tos-fda-disclaimer',
      title: 'FDA Supplement Disclaimer',
      required: true,
      keywords: ['food and drug administration', 'not intended to diagnose'],
      weight: 'critical',
    },
    // ── HIGH (30% of score) ──
    {
      id: 'tos-reconsent',
      title: 'Re-Consent Gate',
      required: true,
      keywords: ['re-consent', 'affirmative', 'material changes'],
      weight: 'high',
    },
    {
      id: 'tos-cooling-off',
      title: 'Account Deletion Cooling-Off',
      required: true,
      keywords: ['cooling-off', '24-hour', 'deletion request'],
      weight: 'high',
    },
    {
      id: 'tos-deletion-sla',
      title: 'Deletion Completion SLA',
      required: true,
      keywords: ['30 days', 'deletion will be completed', 'deletion completion'],
      weight: 'high',
    },
    {
      id: 'tos-ip-beneficiary',
      title: 'IP Owner Third-Party Beneficiary',
      required: true,
      keywords: ['third-party beneficiary', 'ip owner', 'intellectual property owner'],
      weight: 'high',
    },
    {
      id: 'tos-per-product-refund',
      title: 'Per-Product Refund Policy',
      required: true,
      keywords: ['non-refundable once', 'unopened', '30-day return'],
      weight: 'high',
    },
    {
      id: 'tos-informal-resolution',
      title: 'Informal Resolution Period',
      required: true,
      keywords: ['thirty (30) days', 'informal', 'good-faith'],
      weight: 'high',
    },
    {
      id: 'tos-hipaa-npp',
      title: 'HIPAA NPP Reference',
      required: true,
      keywords: ['notice of privacy practices', 'hipaa'],
      weight: 'high',
    },
    // ── v2 ADDITIONS ──
    {
      id: 'tos-section-count',
      title: 'All 13 Sections Present',
      required: true,
      keywords: ['## 13', 'general provisions'],
      weight: 'high',
    },
    {
      id: 'tos-no-bracket-placeholders',
      title: 'No Bracket Placeholders (Variables Resolved)',
      required: true,
      keywords: ['legal@', 'support@', 'privacy@'],
      weight: 'high',
    },
    {
      id: 'tos-assignment-ma',
      title: 'Assignment Clause (M&A)',
      required: true,
      keywords: ['merger', 'acquisition', 'sale of all or substantially all assets'],
      weight: 'high',
    },
    {
      id: 'tos-data-export',
      title: 'Data Export Right',
      required: true,
      keywords: ['data export', 'request a copy', 'portable'],
      weight: 'high',
    },
    // ── v3 ADDITIONS: Tier Pricing Alignment ──
    {
      id: 'tos-free-tier-language',
      title: 'Free Tier Language (if applicable)',
      required: true,
      keywords: ['no charge', 'at no cost', 'free to registered users', 'provided at no charge'],
      weight: 'high',
    },
    {
      id: 'tos-paid-tiers-refund',
      title: 'Paid Tiers Have Refund Language',
      required: true,
      keywords: ['non-refundable', 'refund', 'return'],
      weight: 'high',
    },
  ],
};

// ── PRIVACY POLICY QUALITY GATE ──
const PRIVACY_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    {
      id: 'pp-data-categories',
      title: 'Data Categories (PHI + PII)',
      required: true,
      keywords: ['lab results', 'health data', 'personal information', 'biomarker'],
      weight: 'critical',
    },
    {
      id: 'pp-hipaa',
      title: 'HIPAA Compliance',
      required: true,
      keywords: ['hipaa', 'protected health information', 'phi', 'covered entity'],
      weight: 'critical',
    },
    {
      id: 'pp-ccpa',
      title: 'CCPA/CPRA Rights',
      required: true,
      keywords: ['ccpa', 'california', 'right to delete', 'right to know', 'opt-out'],
      weight: 'critical',
    },
    {
      id: 'pp-mhmda',
      title: 'MHMDA/CTDPA Reference',
      required: true,
      keywords: ['my health my data', 'mhmda', 'washington', 'consumer health data'],
      weight: 'critical',
    },
    {
      id: 'pp-retention',
      title: 'Data Retention Periods',
      required: true,
      keywords: ['retention', '7 year', 'six year', '3 year', 'retention period'],
      weight: 'critical',
    },
    {
      id: 'pp-third-party',
      title: 'Third-Party Data Sharing',
      required: true,
      keywords: ['third party', 'share', 'service provider', 'processor'],
      weight: 'high',
    },
    {
      id: 'pp-encryption',
      title: 'Security Measures',
      required: true,
      keywords: ['encryption', 'aes-256', 'encrypted', 'security measures'],
      weight: 'high',
    },
    {
      id: 'pp-no-sale',
      title: 'No Sale of Data',
      required: true,
      keywords: ['do not sell', 'not sell', 'no sale'],
      weight: 'high',
    },
    {
      id: 'pp-minors',
      title: 'Children/Minors Policy',
      required: true,
      keywords: ['children', 'minor', 'under 18', 'coppa'],
      weight: 'high',
    },
    {
      id: 'pp-breach',
      title: 'Breach Notification',
      required: true,
      keywords: ['breach', 'notification', 'data breach', 'security incident'],
      weight: 'high',
    },
  ],
};

// ── CONSUMER HEALTH DATA QUALITY GATE ──
const CHD_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'chd-mhmda-definition', title: 'MHMDA Definition', required: true, keywords: ['consumer health data', 'my health my data', 'mhmda', '19.373'], weight: 'critical' },
    { id: 'chd-data-categories', title: 'Data Categories Listed', required: true, keywords: ['lab results', 'biomarker', 'health questionnaire', 'prescription'], weight: 'critical' },
    { id: 'chd-collection-purposes', title: 'Collection Purposes', required: true, keywords: ['collection purpose', 'purpose of collection', 'why we collect', 'collected for'], weight: 'critical' },
    { id: 'chd-affirmative-consent', title: 'Affirmative Opt-In Consent', required: true, keywords: ['affirmative consent', 'opt-in', 'affirmative opt-in'], weight: 'critical' },
    { id: 'chd-user-rights', title: 'User Rights (Access/Delete/Withdraw)', required: true, keywords: ['right to access', 'right to delete', 'withdraw consent', 'deletion'], weight: 'critical' },
    { id: 'chd-geofencing', title: 'Geofencing Prohibition', required: true, keywords: ['geofenc', '1750', 'health facilit'], weight: 'critical' },
    { id: 'chd-ctdpa', title: 'CTDPA Reference', required: true, keywords: ['connecticut', 'ctdpa', 'connecticut data privacy'], weight: 'critical' },
    { id: 'chd-data-sharing', title: 'Data Sharing with Partners', required: true, keywords: ['third party', 'share', 'service provider', 'partner'], weight: 'high' },
    { id: 'chd-response-window', title: '30-Day Response Window', required: true, keywords: ['30 day', 'thirty', 'response'], weight: 'high' },
    { id: 'chd-contact', title: 'Privacy Contact Information', required: true, keywords: ['privacy@', 'contact'], weight: 'high' },
    { id: 'chd-standalone', title: 'Standalone Policy Structure', required: true, keywords: ['consumer health data policy', 'health data policy'], weight: 'high' },
  ],
};

// ── TELEHEALTH INFORMED CONSENT QUALITY GATE ──
const TELEHEALTH_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'th-nature-services', title: 'Nature of Telehealth Services', required: true, keywords: ['telehealth', 'telemedicine', 'asynchronous', 'consultation'], weight: 'critical' },
    { id: 'th-benefits-risks', title: 'Benefits and Risks', required: true, keywords: ['benefit', 'risk', 'limitation'], weight: 'critical' },
    { id: 'th-right-refuse', title: 'Right to Refuse/Withdraw', required: true, keywords: ['right to refuse', 'withdraw', 'discontinue', 'revoke'], weight: 'critical' },
    { id: 'th-emergency', title: 'Emergency Protocols', required: true, keywords: ['emergency', '911', 'life-threatening'], weight: 'critical' },
    { id: 'th-provider-quals', title: 'Provider Qualifications', required: true, keywords: ['licensed', 'qualified', 'credential', 'provider'], weight: 'critical' },
    { id: 'th-consent-capture', title: 'Consent/Signature Capture', required: true, keywords: ['signature', 'e-signature', 'checkbox', 'i have read', 'i understand'], weight: 'critical' },
    { id: 'th-privacy', title: 'Privacy and Security', required: true, keywords: ['privacy', 'confidential', 'secure', 'hipaa'], weight: 'high' },
    { id: 'th-no-physical', title: 'Limitations (No Physical Exam)', required: true, keywords: ['physical exam', 'in-person', 'hands-on'], weight: 'high' },
    { id: 'th-records', title: 'Record Keeping', required: true, keywords: ['record', 'medical record', 'documentation'], weight: 'high' },
    { id: 'th-med-director', title: 'Medical Director Oversight', required: true, keywords: ['medical director', 'oversight', 'clinical oversight'], weight: 'high' },
    { id: 'th-complaint', title: 'Complaint Process', required: true, keywords: ['complaint', 'grievance', 'concern'], weight: 'high' },
  ],
};

export const SEED_TEMPLATES_USER_LEGAL: DocTemplate[] = [
  // ── 1.1 Terms of Service ──
  {
    id: '1.1-terms-of-service',
    categoryId: 'user-facing-legal',
    title: 'Terms of Service',
    description:
      'Platform terms of service governing user access, acceptable use, liability limitations, and dispute resolution.',
    templateContent: `# Terms of Service

**{{brandName}}** — operated by {{brandEntity}}

Effective Date: {{effectiveDate}}

## 1. Acceptance of Terms and Re-Consent
{{AI_GENERATED_CONTENT}}

## 2. Eligibility and Age Requirements
{{AI_GENERATED_CONTENT}}

## 3. Description of Services
{{AI_GENERATED_CONTENT}}

## 4. Medical and AI Disclaimers
{{AI_GENERATED_CONTENT}}

## 5. Consent Model
{{AI_GENERATED_CONTENT}}

## 6. Account Management and Deletion
{{AI_GENERATED_CONTENT}}

## 7. Intellectual Property
{{AI_GENERATED_CONTENT}}

## 8. Payment, Billing, and Refunds
{{AI_GENERATED_CONTENT}}

## 9. Limitation of Liability
{{AI_GENERATED_CONTENT}}

## 10. Dispute Resolution and Arbitration
{{AI_GENERATED_CONTENT}}

## 11. Termination
{{AI_GENERATED_CONTENT}}

## 12. Privacy and Regulatory References
{{AI_GENERATED_CONTENT}}

## 13. General Provisions
{{AI_GENERATED_CONTENT}}

## Contact Us
Questions? Contact us at {{legalEmail}}.
`,
    requiredVariables: [
      'brandName',
      'brandEntity',
      'brandDomain',
      'legalEmail',
      'privacyEmail',
      'supportEmail',
      'stateOfFormation',
      'medicalDirectorName',
      'consultationFee',
    ],
    generationPrompt: `Generate comprehensive Terms of Service for {{brandName}} ({{brandEntity}}), an AI-powered health analysis platform at {{brandDomain}} in the {{marketVertical}} space.

=== OUTPUT FORMAT RULES ===
- Output the complete document as a single continuous document with sequential section numbering.
- Each section begins with a single heading (e.g., "## 1. Acceptance and Re-Consent").
- Do NOT generate outline stubs, table of contents headers, topic headers, or duplicate section headers between or before sections.
- Do NOT output a summary, overview, or section list before the document body.
- Number sections sequentially starting at 1. Subsections use ### format.
- The document begins with the title block then immediately Section 1.

=== SECTION COMPLETENESS RULE ===
- Every section listed in the blueprint MUST appear with complete content.
- Never truncate a section mid-sentence. Never omit a section due to length.
- If approaching output limits, reduce detail in earlier sections rather than omitting later sections.
- Priority: ALL 13 sections present and complete > any individual section being exhaustively detailed.

=== TEMPLATE VARIABLE RULES ===
- All placeholder values MUST use mustache format: {{variableName}}
- NEVER use [INSERT], [TBD], [PLACEHOLDER], or any bracket notation.
- Every variable must map to a brand config field. Standard variable map:
  {{effectiveDate}}, {{lastUpdatedDate}}, {{brandEntity}}, {{brandName}},
  {{stateOfFormation}}, {{arbitrationCounty}}, {{medicalDirectorName}},
  {{telehealthNetwork}}, {{consultationFee}},
  {{paymentProcessor}}, {{privacyPolicyUrl}}, {{chdPolicyUrl}}, {{hipaaNoticeUrl}},
  {{telehealthConsentUrl}}, {{cookiePolicyUrl}}, {{supportEmail}}, {{privacyEmail}},
  {{legalEmail}}, {{dmcaEmail}}, {{businessAddress}}
- CONDITIONAL VARIABLES (only use when tier pricingModel = "paid"):
  {{tier1Price}}, {{tier1PriceDescription}}
- If a value is needed that has no variable above, use {{camelCaseVar}} format inline. Do NOT add any notes, lists, or commentary about new variables.

=== TIER PRICING RULES ===
- The TIER CONFIGURATION block appended to this prompt specifies each tier's pricingModel.
- If pricingModel = "free":
    - State clearly: "This service tier is provided at no charge to registered users."
    - Do NOT generate pricing, payment terms, or refund policy for this tier.
    - Do NOT include this tier in Section 8 (Payment and Refund).
    - Do NOT use {{tier1Price}} or any price variable for this tier.
- If pricingModel = "paid":
    - Include pricing using the appropriate {{variable}} from brand config.
    - Include tier-specific refund policy.
    - Include this tier in Section 8.
- If pricingModel = "freemium":
    - State the base service is provided at no charge.
    - Describe the paid upgrade option with pricing.
    - Include refund policy for the paid component only.
- Section 8 (Payment and Refund) should ONLY include tiers where pricingModel is NOT "free".

=== ENTITY ALIGNMENT RULE ===
- Use ONLY entity names from the brand config.
- {{telehealthNetwork}} is a nationwide telehealth provider network that provides clinical services through its own affiliated professional corporation(s) in each state.
- {{brandEntity}} contracts directly with {{telehealthNetwork}}. There is no intermediary professional corporation in the active structure.
- {{medicalDirectorName}} provides clinical protocol oversight and quality assurance within {{brandEntity}}. Do not say "supervises" — use "provides clinical oversight."
- Do NOT reference dormant or backup entities (like CC Medical PC) in user-facing documents.
- CORRECT Tier 2 pattern: "Telehealth consultations are provided through {{telehealthNetwork}}. Clinical services are delivered by licensed healthcare practitioners in the {{telehealthNetwork}} provider network. {{medicalDirectorName}} serves as Medical Director of {{brandEntity}}, providing clinical protocol oversight."

Start the document with exactly:

# Terms of Service
**{{brandName}}** — operated by {{brandEntity}}
Effective Date: {{effectiveDate}} | Last Updated: {{lastUpdatedDate}}

=== BUSINESS CONTEXT ===
- State of formation: {{stateOfFormation}}
- Platform is an educational comparison tool — does NOT diagnose, treat, or prescribe
- Three service tiers: (1) AI lab analysis, (2) telehealth via {{telehealthNetwork}}, (3) supplement marketplace
- Consultation fee: \${{consultationFee}}
- IP: platform technology licensed to {{brandEntity}} by the intellectual property owner under exclusive license

=== DOCUMENT BLUEPRINT: 13 MANDATORY SECTIONS ===

## 1. Acceptance and Re-Consent
- Binding agreement: "By accessing or using" trigger
- Right to modify terms with email + platform notification
- MATERIAL CHANGES require affirmative re-consent (NOT continued use = acceptance)
- Effective date + last updated date

## 2. Eligibility and Age Requirements
- 18+ age, US residency, legal capacity
- MISREPRESENTATION: "If we determine you have misrepresented your age, we will immediately terminate your account and delete all associated data, including any health data, within 24 hours."
- Right to verify eligibility at any time

## 3. Description of Services
- Tier 1 (AI Analysis): "educational comparison tool", AI disclaimer ("may contain errors, omissions, or inaccuracies"), "does NOT diagnose", "for informational and educational purposes only".
  IF tier1 pricingModel = "free": State "This service is provided at no charge to registered users of the Service." No pricing language.
  IF tier1 pricingModel = "paid": Include pricing: {{tier1Price}}. {{tier1PriceDescription}}
  All disclaimers apply regardless of pricing model.
- Tier 2 (Telehealth): {{telehealthNetwork}} provider network, {{medicalDirectorName}} as Medical Director providing clinical oversight, \${{consultationFee}} per consultation, "does not guarantee a prescription will be issued — prescribing decisions are made by the treating provider based on independent clinical judgment", separate Telehealth Informed Consent required, provider-patient relationship is with the treating provider NOT with {{brandName}}
- Tier 3 (Supplements): FDA disclaimer ("not evaluated by the Food and Drug Administration", "not intended to diagnose, treat, cure, or prevent any disease"), "consult your healthcare provider before starting any supplement regimen"

## 4. Medical and AI Disclaimers
- {{brandEntity}} is a technology platform company, NOT a medical practice
- "Does not practice medicine"
- "Does not employ prescribing physicians"
- "Does not control or direct clinical decisions"
- AI output may contain errors, is not medical advice
- No provider-patient relationship from Tier 1/Tier 3 use alone
- Emergency: call 911

## 5. Consent Model
Enumerate seven (7) consent purposes:
1. Core service delivery (REQUIRED)
2. Health data analysis / AI processing (REQUIRED)
3. Provider services / sharing with telehealth network (REQUIRED for Tier 2)
4. Supplement recommendations / personalization (REQUIRED for Tier 3)
5. Marketing communications (OPTIONAL, opt-in)
6. Research and analytics / de-identified (OPTIONAL)
7. Third-party data sharing (OPTIONAL)
- Withdrawal: "You may withdraw consent for optional purposes (5-7) at any time. Withdrawal of required consents (1-4) will result in account deactivation."
- Re-consent: "Material changes to how we process your data will require your affirmative re-consent. We will not rely on continued use as implied consent for material data processing changes."

## 6. Account Management and Deletion
- Account creation and security responsibilities
- Deletion with 24-hour cooling-off period: "Upon receiving your deletion request, we will initiate a 24-hour cooling-off period. After the cooling-off period, account deletion will be completed within 30 days."
- Data export right: user may request a copy in portable format before deletion
- Retention exceptions: legal obligations, fraud prevention, dispute resolution

## 7. Intellectual Property
- Platform technology owned by third-party intellectual property owner ("IP Owner")
- Licensed to {{brandEntity}} under exclusive license agreement
- User gets limited, non-exclusive, revocable license
- Standard restrictions (no reverse engineering, scraping, etc.)
- IP Owner as express third-party beneficiary
- Feedback license to both {{brandEntity}} AND IP Owner
- DMCA compliance: send notices to {{dmcaEmail}}

## 8. Payment, Billing, and Refunds
- Payment processor: {{paymentProcessor}}
- ONLY include tiers where pricingModel is NOT "free" (check TIER CONFIGURATION above).
  IF tier1 pricingModel = "free": OMIT Tier 1 from this section entirely.
  IF tier1 pricingModel = "paid": Include AI Analysis: {{tier1Price}}. Refund within 7 days if technical error; no refund once report viewed.
- Per-tier pricing with refund policies for PAID tiers:
  * Telehealth: \${{consultationFee}}. Non-refundable once completed. Cancellation 24+ hours before = full refund.
  * Supplements: 30-day return for unopened products. Opened non-refundable except defect/error.
  * Prescriptions: Non-refundable once dispensed. Exception for pharmacy error or defect.
- "Consultation fee does not guarantee prescription will be issued"

## 9. Limitation of Liability
- Standard limitation + disclaimer of warranties
- CLINICAL LIABILITY CARVE-OUT: "{{brandName}} is not liable for clinical decisions, diagnoses, prescriptions, or medical advice provided by healthcare practitioners through the Service. Liability for clinical services rests solely with the treating provider and, where applicable, the telehealth network."
- Cap: greater of $100 or 12 months fees paid
- Jurisdictional savings clause

## 10. Dispute Resolution and Arbitration
- Thirty (30) day informal resolution period with good-faith negotiation at {{legalEmail}}
- Binding arbitration (AAA Consumer Arbitration Rules) in {{arbitrationCounty}}, {{stateOfFormation}}
- CLASS ACTION WAIVER (bold/caps): "YOU AGREE TO RESOLVE DISPUTES ON AN INDIVIDUAL BASIS ONLY. YOU WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS ACTION."
- 30-day opt-out right from first acceptance
- Small claims + IP/injunctive relief exceptions
- Governing law: {{stateOfFormation}}
- Jury trial waiver

## 11. Termination
- By user: process referencing Section 6 deletion
- By company: grounds (violation, fraud, misrepresentation, inactivity) and process
- Data handling: what deleted (within 30 days), what retained (legal/fraud/disputes), data export right, retention timeline
- Surviving provisions: Sections 7-13

## 12. Privacy and Regulatory References
- Privacy Policy: {{privacyPolicyUrl}} or {{privacyEmail}}
- Consumer Health Data Policy (MHMDA): {{chdPolicyUrl}}
- HIPAA Notice of Privacy Practices: {{hipaaNoticeUrl}} (for Tier 2 users)
- Telehealth Informed Consent: {{telehealthConsentUrl}} (for Tier 2 users)
- Cookie Policy: {{cookiePolicyUrl}}
- CCPA/CPRA rights notice for California residents

## 13. General Provisions
ALL of the following subsections are REQUIRED:
- 13.1 Entire Agreement: Terms + Privacy Policy + CHD Policy + tier-specific consents = complete agreement
- 13.2 Severability: invalid provisions modified to minimum extent or severed
- 13.3 Waiver: failure to enforce is not waiver; waiver only effective in writing
- 13.4 Assignment: User may NOT assign. {{brandEntity}} MAY assign freely, including in connection with merger, acquisition, corporate reorganization, or sale of all or substantially all assets. Acquirer steps into company's shoes automatically.
- 13.5 No Third-Party Beneficiaries: EXCEPT the IP Owner as specified in Section 7
- 13.6 Force Majeure: natural disasters, war, pandemic, government action, internet failures
- 13.7 Notices: to user via email on file; to {{brandEntity}} via {{legalEmail}} or {{businessAddress}}
- 13.8 Headings and Interpretation: headings for convenience; "including" means "without limitation"
- 13.9 Contact: {{supportEmail}}, {{privacyEmail}}, {{legalEmail}}, {{dmcaEmail}}, {{businessAddress}}

End the document with:
## Contact Us
Questions? Contact us at {{legalEmail}}.

FORMAT: Professional legal language. Each section MUST contain ALL listed elements. Attorney picks up a red pen, not a keyboard.
The document MUST end after "Contact Us" — do NOT append any notes, variable lists, or commentary after the final section.`,
    qualityGate: TOS_QUALITY_GATE,
    attorneyReviewRequired: true,
    launchBlocker: true,
    estimatedPages: 12,
    sortOrder: 1,
    // v4: TOS is the anchor document — Round 1, no dependencies
    generationRound: 1,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: ['cpom-firewall', 'ip-licensed-model', 'clinical-liability-carveout'],
  },

  // ── 1.2 Privacy Policy ──
  {
    id: '1.2-privacy-policy',
    categoryId: 'user-facing-legal',
    title: 'Privacy Policy',
    description:
      'Platform privacy policy detailing data collection, use, sharing, retention, and user rights under applicable privacy laws.',
    templateContent: `# Privacy Policy

**{{brandName}}** — operated by {{brandEntity}}

Effective Date: {{effectiveDate}}

## Information We Collect
{{AI_GENERATED_CONTENT}}

## How We Use Your Information
{{AI_GENERATED_CONTENT}}

## How We Share Your Information
{{AI_GENERATED_CONTENT}}

## Data Retention
{{AI_GENERATED_CONTENT}}

## Your Privacy Rights
{{AI_GENERATED_CONTENT}}

## Security
{{AI_GENERATED_CONTENT}}

## Children's Privacy
{{AI_GENERATED_CONTENT}}

## State-Specific Disclosures
{{AI_GENERATED_CONTENT}}

## Changes to This Policy
{{AI_GENERATED_CONTENT}}

## Contact Us
For privacy inquiries, contact us at {{privacyEmail}}.
`,
    requiredVariables: [
      'brandName',
      'brandEntity',
      'brandDomain',
      'privacyEmail',
      'legalEmail',
      'stateOfFormation',
    ],
    generationPrompt: `Generate comprehensive Privacy Policy for {{brandName}} ({{brandEntity}}), an AI-powered health analysis platform at {{brandDomain}} in the {{marketVertical}} space.

Start the document with this exact header:

# Privacy Policy
**{{brandName}}** — operated by {{brandEntity}}
Effective Date: {{effectiveDate}}

Then generate the sections below.

BUSINESS CONTEXT:
- State: {{stateOfFormation}}
- The platform is a comparison and educational tool
- Data processing conducted by {{brandEntity}} as the operating entity
- Platform technology is licensed to {{brandEntity}} by the intellectual property owner

DATA CATEGORIES (all must be addressed):
1. Account info (name, email, phone, DOB, sex)
2. Health data (lab results via PDF/photo upload, health questionnaire, medications, conditions, allergies, health goals, biomarker data)
3. AI analysis outputs (biomarker comparisons, supplement recommendations)
4. Telehealth records (consultation notes via {{telehealthNetwork}} and {{pcEntityName}})
5. Purchase history (supplements, consultations)
6. Technical data (device, browser, IP, Firebase analytics)

THIRD-PARTY SHARING (name each):
- {{pcEntityName}} — clinical data for telehealth (as service provider)
- {{telehealthNetwork}} — telehealth network
- Pharmacy/fulfillment partner — prescription/supplement orders
- {{paymentProcessor}} — payment processing
- {{cloudProvider}} — infrastructure

MANDATORY PROVISIONS:

1. DATA CATEGORIES [CRITICAL]: List all data types including lab results, health data, personal information, and biomarker data.

2. HIPAA COMPLIANCE [CRITICAL]: Address HIPAA requirements. Reference protected health information (PHI) through {{pcEntityName}} as covered entity.

3. CCPA/CPRA RIGHTS [CRITICAL]: California consumer rights — right to know, right to delete, right to opt-out of sale, right to non-discrimination.

4. MHMDA/CTDPA REFERENCE [CRITICAL]: Reference Washington My Health My Data Act (MHMDA) and consumer health data obligations. Cross-reference standalone Consumer Health Data Policy.

5. DATA RETENTION PERIODS [CRITICAL]: Specify retention periods — health data 7 years, account data 3 years post-deletion, audit logs 6 years.

6. THIRD-PARTY SHARING [HIGH]: Name all third-party data recipients and their purposes. Use "service provider" and "processor" language.

7. SECURITY MEASURES [HIGH]: AES-256 encryption, SOC 2 hosting, encrypted at rest and in transit.

8. NO SALE OF DATA [HIGH]: Explicitly state "We do not sell your personal data."

9. CHILDREN/MINORS [HIGH]: Under 18 not permitted. Minor detection and removal procedures.

10. BREACH NOTIFICATION [HIGH]: Data breach notification procedures and timeline.

End the document with:
## Contact Us
For privacy inquiries, contact us at {{privacyEmail}}.

FORMAT: Professional legal language. Clear ## section headers. Attorney-review ready. Full Markdown.`,
    qualityGate: PRIVACY_QUALITY_GATE,
    attorneyReviewRequired: true,
    launchBlocker: true,
    estimatedPages: 10,
    sortOrder: 2,
    generationRound: 2,
    dependencies: ['1.1-terms-of-service'],
    anchorSections: ['§3', '§5', '§6', '§12'],
    inheritedProvisions: ['cpom-firewall', 'consent-model-reference'],
  },

  // ── 1.3 Consumer Health Data Policy ──
  {
    id: '1.3-consumer-health-data-policy',
    categoryId: 'user-facing-legal',
    title: 'Consumer Health Data Policy (MHMDA + CTDPA)',
    description:
      'Standalone consumer health data policy required by Washington My Health My Data Act and Connecticut Data Privacy Act. Must be a separate page, not embedded in the Privacy Policy.',
    templateContent: `# Consumer Health Data Policy

**{{brandName}}** — operated by {{brandEntity}}

This Consumer Health Data Policy describes how {{brandEntity}} collects, uses, shares, and protects consumer health data as defined under applicable state laws including the Washington My Health My Data Act (MHMDA) and Connecticut Data Privacy Act (CTDPA).

## Table of Contents
- [Definitions](#definitions)
- [Categories of Health Data Collected](#categories)
- [Purposes of Collection](#purposes)
- [Consent Model](#consent)
- [Your Rights](#rights)
- [Data Sharing](#sharing)
- [Contact Us](#contact)

---

## Definitions
{{AI_GENERATED_CONTENT}}

## Categories of Health Data Collected
{{AI_GENERATED_CONTENT}}

## Purposes of Collection
{{AI_GENERATED_CONTENT}}

## Consent Model
{{AI_GENERATED_CONTENT}}

## Your Rights
{{AI_GENERATED_CONTENT}}

## Data Sharing
{{AI_GENERATED_CONTENT}}

## Geofencing Prohibition
{{AI_GENERATED_CONTENT}}

## Contact Us
For questions about this policy, contact us at {{privacyEmail}}.
`,
    requiredVariables: [
      'brandName',
      'brandEntity',
      'brandDomain',
      'privacyEmail',
      'stateOfFormation',
      'marketVertical',
      'pcEntityName',
    ],
    generationPrompt:
      'Generate a Consumer Health Data Policy compliant with Washington MHMDA §19.373.005 and Connecticut CTDPA for {{brandEntity}} at {{brandDomain}}. AI-powered health analysis platform in {{marketVertical}}. Collects: bloodwork lab results, health questionnaire responses, supplement purchase history, provider consultation records, prescription info. Requirements: define consumer health data per MHMDA, list all data categories, state each collection purpose, describe affirmative opt-in consent model, explain user rights (access, deletion, withdrawal), describe data sharing with {{pcEntityName}} and telehealth/pharmacy partners, 30-day response window, geofencing prohibition (1750ft from health facilities), contact {{privacyEmail}}. Must be standalone page, not embedded in Privacy Policy. Format: HTML with clean styling, TOC with anchor links.',
    qualityGate: CHD_QUALITY_GATE,
    attorneyReviewRequired: true,
    launchBlocker: true,
    estimatedPages: 8,
    sortOrder: 3,
    generationRound: 2,
    dependencies: ['1.1-terms-of-service'],
    anchorSections: ['§5', '§6', '§12'],
    inheritedProvisions: ['consent-model-reference'],
  },

  // ── 1.4 HIPAA Notice of Privacy Practices ──
  {
    id: '1.4-hipaa-npp',
    categoryId: 'user-facing-legal',
    qualityGate: {
      requiredScore: 90,
      maxAttempts: 3,
      provisions: [
        // Uses and Disclosures (critical — core of NPP)
        { id: 'npp-treatment', title: 'PHI Use for Treatment', required: true, keywords: ['treatment', 'health care provider', 'medical care', 'clinical'], weight: 'critical' },
        { id: 'npp-payment', title: 'PHI Use for Payment', required: true, keywords: ['payment', 'billing', 'insurance', 'health plan'], weight: 'critical' },
        { id: 'npp-operations', title: 'PHI Use for Operations', required: true, keywords: ['health care operations', 'operations', 'quality assurance', 'quality improvement'], weight: 'critical' },
        { id: 'npp-authorization', title: 'Authorization Requirements', required: true, keywords: ['authorization', 'written authorization', 'written permission', 'your authorization'], weight: 'critical' },
        // Patient Rights (critical — HIPAA mandated)
        { id: 'npp-right-access', title: 'Right to Access PHI', required: true, keywords: ['right to access', 'access to', 'inspect', 'obtain a copy', 'request access'], weight: 'critical' },
        { id: 'npp-right-amend', title: 'Right to Amend PHI', required: true, keywords: ['amend', 'amendment', 'correct', 'request a change'], weight: 'critical' },
        { id: 'npp-right-accounting', title: 'Right to Accounting of Disclosures', required: true, keywords: ['accounting of disclosures', 'accounting', 'list of disclosures'], weight: 'critical' },
        { id: 'npp-right-restrict', title: 'Right to Request Restrictions', required: true, keywords: ['restrict', 'restriction', 'limit', 'request restriction'], weight: 'high' },
        { id: 'npp-right-confidential', title: 'Right to Confidential Communications', required: true, keywords: ['confidential communication', 'alternative means', 'alternative location', 'communicate with you'], weight: 'high' },
        { id: 'npp-right-complaint', title: 'Right to File Complaint', required: true, keywords: ['complaint', 'file a complaint', 'hhs', 'secretary', 'department of health'], weight: 'critical' },
        // Covered Entity Duties (high)
        { id: 'npp-duty-notice', title: 'Duty to Provide Notice', required: true, keywords: ['provide this notice', 'abide by', 'bound by', 'duty', 'obligation'], weight: 'high' },
        { id: 'npp-breach-notify', title: 'Breach Notification Duty', required: true, keywords: ['breach', 'notification', 'notify you', 'notify'], weight: 'high' },
        // Regulatory References (high)
        { id: 'npp-hipaa-ref', title: 'HIPAA/CFR Reference', required: true, keywords: ['hipaa', '45 cfr', '164.520', 'health insurance portability'], weight: 'high' },
        { id: 'npp-effective-date', title: 'Effective Date', required: true, keywords: ['effective date', 'effective'], weight: 'high' },
        { id: 'npp-contact', title: 'Privacy Officer Contact', required: true, keywords: ['privacy officer', 'contact', 'privacy'], weight: 'high' },
      ],
    } satisfies QualityGate,
    title: 'Notice of Privacy Practices (HIPAA NPP)',
    description:
      'HIPAA-required Notice of Privacy Practices for the Professional Corporation (PC) detailing how protected health information is used and disclosed.',
    templateContent: `# Notice of Privacy Practices

**{{pcEntityName}}**

THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE REVIEW IT CAREFULLY.

Effective Date: {{effectiveDate}}

## Uses and Disclosures of PHI
{{AI_GENERATED_CONTENT}}

## Your Rights
{{AI_GENERATED_CONTENT}}

## Our Duties
{{AI_GENERATED_CONTENT}}

## Contact Information
Privacy Officer: {{privacyEmail}}
Address: {{businessAddress}}
`,
    requiredVariables: [
      'pcEntityName',
      'pcOwnerName',
      'brandEntity',
      'privacyEmail',
      'legalEmail',
      'businessAddress',
      'effectiveDate',
    ],
    generationPrompt:
      'Generate HIPAA Notice of Privacy Practices for {{pcEntityName}} per 45 CFR §164.520. PC provides clinical oversight through affiliated MSO ({{brandEntity}}). Uses telehealth network for consultations and pharmacy partner for fulfillment. Cover: PHI use for treatment/payment/operations, patient rights (access, amendment, accounting of disclosures, restriction requests, confidential communications, complaint), when authorization required vs not, covered entity duties, contact {{privacyEmail}} and {{businessAddress}}, effective date {{effectiveDate}}, right to change notice with re-notification. Format: clean HTML, accessible, printable.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 6,
    sortOrder: 4,
    generationRound: 3,
    dependencies: ['1.1-terms-of-service', '1.2-privacy-policy'],
    anchorSections: ['§3', '§4', '§5', '§12'],
    inheritedProvisions: ['consent-model-reference'],
  },

  // ── 1.5 Cookie Policy ──
  {
    id: '1.5-cookie-policy',
    categoryId: 'user-facing-legal',
    title: 'Cookie Policy',
    description:
      'Cookie policy detailing all cookies used on the platform, their purposes, durations, and user controls for consent management.',
    templateContent: `# Cookie Policy

**{{brandName}}** ({{brandDomain}})

This Cookie Policy explains how {{brandName}} uses cookies and similar tracking technologies.

## Cookie Categories
{{AI_GENERATED_CONTENT}}

## Cookie Inventory
| Cookie Name | Category | Purpose | Duration |
|-------------|----------|---------|----------|
{{AI_GENERATED_CONTENT}}

## Your Controls
{{AI_GENERATED_CONTENT}}

## Contact Us
For questions about cookies, contact us at {{privacyEmail}}.
`,
    requiredVariables: ['brandName', 'brandDomain', 'privacyEmail'],
    generationPrompt:
      'Generate Cookie Policy for {{brandName}} ({{brandDomain}}). Uses: essential cookies (session, auth), analytics (Firebase/Google Analytics), preference (settings, theme), marketing (opt-in only). Requirements: categorize all cookies (essential, functional, analytics, marketing), explain purpose and duration, user controls for non-essential, reference Privacy Policy and CHD Policy, GDPR cookie compliance for EU visitors, CCPA disclosure requirements. Contact: {{privacyEmail}}. Format: clean HTML, table format for cookie inventory.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 3,
    sortOrder: 5,
    generationRound: 4,
    dependencies: ['1.2-privacy-policy'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 1.6 Refund & Return Policy ──
  {
    id: '1.6-refund-return-policy',
    categoryId: 'user-facing-legal',
    title: 'Refund & Return Policy',
    description:
      'Standalone refund and return policy covering supplements, provider consultations, and prescription medications.',
    templateContent: `# Refund & Return Policy

**{{brandName}}** — operated by {{brandEntity}}

## Supplements
{{AI_GENERATED_CONTENT}}

## Provider Consultations
{{AI_GENERATED_CONTENT}}

## Prescription Medications
{{AI_GENERATED_CONTENT}}

## How to Request a Refund
Contact us at {{supportEmail}}.
`,
    requiredVariables: [
      'brandName',
      'brandEntity',
      'supportEmail',
      'consultationFee',
    ],
    generationPrompt:
      'Generate standalone Refund & Return Policy for {{brandName}} ({{brandEntity}}). Three product categories: 1) Supplements: 30-day return for unopened/sealed, shipping non-refundable, opened non-refundable except defects. 2) Provider consultations (${{consultationFee}}): non-refundable once completed, refundable if provider unavailable, no refund for clinical decisions. 3) Prescription medications: non-refundable once dispensed, refundable for pharmacy error/defects only. Contact: {{supportEmail}}. Format: clean HTML, clear sections per product type.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 3,
    sortOrder: 6,
    generationRound: 4,
    dependencies: ['1.1-terms-of-service'],
    anchorSections: ['§3', '§8'],
    inheritedProvisions: [],
  },

  // ── 1.7 Telehealth Informed Consent ──
  {
    id: '1.7-telehealth-informed-consent',
    categoryId: 'user-facing-legal',
    title: 'Telehealth Informed Consent',
    description:
      'Patient-facing informed consent for telehealth services including asynchronous consultations, provider qualifications, and emergency protocols. Required before any clinical engagement.',
    templateContent: `# Telehealth Informed Consent

**{{brandName}}** — operated by {{brandEntity}}
Clinical Services provided by {{pcEntityName}}

## Nature of Telehealth Services
{{AI_GENERATED_CONTENT}}

## Benefits and Risks
{{AI_GENERATED_CONTENT}}

## Your Rights
{{AI_GENERATED_CONTENT}}

## Emergency Protocols
{{AI_GENERATED_CONTENT}}

## Provider Qualifications
{{AI_GENERATED_CONTENT}}

## Consent

- [ ] I have read and understand this Telehealth Informed Consent
- [ ] I understand the benefits and risks of telehealth
- [ ] I understand I may withdraw consent at any time

**Patient Signature:** ___________________________

**Date:** ___________________________

Contact: {{supportEmail}}
`,
    requiredVariables: [
      'brandName',
      'brandEntity',
      'pcEntityName',
      'medicalDirectorName',
      'telehealthNetwork',
      'supportEmail',
      'stateOfFormation',
    ],
    generationPrompt:
      'Generate Telehealth Informed Consent for {{brandName}} ({{brandEntity}}). Provider services through {{pcEntityName}} using {{telehealthNetwork}}. Asynchronous consultations (questionnaire-based) with optional synchronous follow-up. Requirements per state telehealth consent laws: nature of services, benefits/risks vs in-person, right to refuse/withdraw, privacy/security, limitations (no physical exam), emergency protocols (call 911), provider qualifications (licensed in patient state via {{pcEntityName}}), Medical Director oversight ({{medicalDirectorName}}), record keeping, technical requirements, billing, complaint process. Must include: checkbox "I have read and understand", signature/e-signature capture, date/timestamp, copy to patient. Format: HTML form with styled checkboxes and signature area, printable, accessible.',
    qualityGate: TELEHEALTH_QUALITY_GATE,
    attorneyReviewRequired: true,
    launchBlocker: true,
    estimatedPages: 5,
    sortOrder: 7,
    generationRound: 4,
    dependencies: ['1.1-terms-of-service', '1.2-privacy-policy', '1.4-hipaa-npp'],
    anchorSections: ['§3', '§4', '§5', '§12'],
    inheritedProvisions: ['cpom-firewall', 'clinical-liability-carveout', 'consent-model-reference'],
  },

  // ── 1.8 Supplement Disclaimer ──
  {
    id: '1.8-supplement-disclaimer',
    categoryId: 'user-facing-legal',
    title: 'Supplement Disclaimer',
    description:
      'FDA-compliant supplement disclaimer displayed on recommendation pages and at checkout. Short-form notice that supplements are not intended to diagnose, treat, cure, or prevent disease.',
    templateContent: `# Supplement Disclaimer

**{{brandName}}**

**IMPORTANT NOTICE**

These statements have not been evaluated by the Food and Drug Administration. These products are not intended to diagnose, treat, cure, or prevent any disease.

{{AI_GENERATED_CONTENT}}

Questions? Contact us at {{supportEmail}}.
`,
    requiredVariables: ['brandName', 'supportEmail'],
    generationPrompt: `Generate an FDA-compliant Supplement Disclaimer for {{brandName}}.

MANDATORY STATEMENTS (these exact phrases MUST appear):
1. "These statements have not been evaluated by the Food and Drug Administration"
2. "These products are not intended to diagnose, treat, cure, or prevent any disease"
3. "Consult your healthcare provider before starting any supplement regimen"
4. "Individual results may vary"

ADDITIONAL REQUIRED CONTENT:
- {{brandName}} is an educational comparison tool that shows lab results compared to published functional medicine optimal ranges
- {{brandName}} does not diagnose medical conditions or prescribe treatments
- Supplement recommendations are generated by AI for educational and informational purposes only
- Recommendations are not medical advice and should not replace professional medical guidance
- Users should discuss any supplements with their treating healthcare provider before use
- {{brandName}} is not responsible for adverse reactions to supplements purchased through the platform
- Contact {{supportEmail}} with questions

FORMAT RULES:
- Use actual brand name ({{brandName}}) and contact ({{supportEmail}}) — NEVER bracket placeholders
- Short, clear language suitable for display as a modal or banner
- Do NOT include meta-commentary or notes about the generation process
- Do NOT reference HTML formatting — output clean markdown text
- End with the contact line`,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 1,
    sortOrder: 8,
    generationRound: 4,
    dependencies: ['1.1-terms-of-service'],
    anchorSections: ['§3'],
    inheritedProvisions: [],
  },
];
